import { rem, Paragraph, Header, Flex } from '@vimeo/bokeh';
import { CircleExclamation } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { useCanSeeDevDetails, useIsVpaas } from '@vimeo/vpaas';

import { environmentConfig } from '../../../configs/EnvironmentConfig';
import { createDomName } from '../../../utils/dom/identifying';
import { JSONTable } from '../../json';
import { LiveErrorPageHeader } from './LiveErrorPageHeader';

interface ILiveErrorPageProps extends IBaseDomElementProps {
  isHeaderEnabled?: boolean;
  title: string;
  message: string;
  error?: Error;
}

export function LiveErrorPage({
  id = createDomName('error-page'),
  className = createDomName('error-page'),
  isHeaderEnabled = true,
  title,
  message,
  error,
}: ILiveErrorPageProps) {
  const isVpaas: boolean = useIsVpaas();
  const canSeeDevDetails: boolean = useCanSeeDevDetails();

  const isHeaderShown: boolean = isHeaderEnabled && !isVpaas;
  const isDevErrorShown: boolean = Boolean(
    error && (canSeeDevDetails || environmentConfig.IS_DEV || environmentConfig.IS_CI),
  );

  return (
    <>
      {isHeaderShown ? <LiveErrorPageHeader /> : null}

      <Flex
        id={id}
        className={className}
        direction={'column'}
        align={'center'}
        justifyContent={isDevErrorShown ? 'center' : 'flex-start'}
        width={'100%'}
        height={'100%'}
        grow={1}
        padding={rem(24)}
        background={'background'}
      >
        <CircleExclamation boxSize={'2xl'} />

        <Header
          id={createDomName(id, 'header')}
          className={createDomName(className, 'header')}
          size={'xl'}
          marginY={rem(8)}
        >
          {title}
        </Header>

        <Paragraph
          id={createDomName(id, 'message')}
          className={createDomName(className, 'message')}
          size={'lg'}
          textAlign={'center'}
          maxWidth={rem(400)}
          marginBottom={rem(16)}
        >
          {message}
        </Paragraph>

        {isDevErrorShown ? (
          <JSONTable
            id={createDomName(id, 'details')}
            className={createDomName(className, 'details')}
            header={<Header size={'sm'}>Error details (staff only):</Header>}
            data={error}
          />
        ) : null}
      </Flex>
    </>
  );
}
